import { useSyncExternalStore } from "react";
import type { Actor, Claim, Comment, Edge, Event, Kind, Label, Member, Node, Plan, ProjectRole, Ref, Snapshot, Space, Template, View } from "@melo/shared";

export interface Tables {
  actors: Map<string, Actor>;
  /** Keyed "spaceId:actorId". */
  members: Map<string, Member>;
  spaces: Map<string, Space>;
  labels: Map<string, Label>;
  templates: Map<string, Template>;
  kinds: Map<string, Kind>;
  nodes: Map<string, Node>;
  edges: Map<string, Edge>;
  claims: Map<string, Claim>;
  refs: Map<string, Ref>;
  plans: Map<string, Plan>;
  views: Map<string, View>;
  comments: Map<string, Comment>;
}
export type TableName = keyof Tables;
type Entity = Tables[TableName] extends Map<string, infer V> ? V : never;

const TABLE_OF: Record<Event["entity"], TableName> = {
  actor: "actors",
  member: "members",
  label: "labels",
  template: "templates",
  space: "spaces",
  kind: "kinds",
  node: "nodes",
  edge: "edges",
  claim: "claims",
  ref: "refs",
  plan: "plans",
  view: "views",
  comment: "comments",
};
const TABLES: TableName[] = ["actors", "members", "labels", "templates", "spaces", "kinds", "nodes", "edges", "claims", "refs", "plans", "views", "comments"];

/** An optimistic change: the entity as the client expects it to be after the server applies the command. Null removes it. */
export interface Patch {
  table: TableName;
  id: string;
  value: Entity | null;
}

export type Connection = "connecting" | "live" | "offline";
export type Status = "loading" | "ready" | "signed-out" | "error";

const emptyTables = (): Tables => ({
  actors: new Map(),
  members: new Map(),
  labels: new Map(),
  templates: new Map(),
  spaces: new Map(),
  kinds: new Map(),
  nodes: new Map(),
  edges: new Map(),
  claims: new Map(),
  refs: new Map(),
  plans: new Map(),
  views: new Map(),
  comments: new Map(),
});

/** Archived nodes and refs, and decided plans, leave the working set just like deletes. */
function isGone(table: TableName, value: Entity): boolean {
  if (table === "nodes" || table === "refs") return Boolean((value as Node | Ref).archivedAt);
  if (table === "plans") return (value as Plan).status !== "open";
  return false;
}

/**
 * The client's copy of everything the user can see. Server events are applied idempotently:
 * each entity remembers the sequence number of its last change, and older events are ignored.
 * Optimistic patches sit on top of the server state until their command returns.
 */
class Store {
  me: Actor | null = null;
  /** The signed-in person's role in each project they can see. */
  myRoles: Record<string, ProjectRole> = {};
  status: Status = "loading";
  error = "";
  connection: Connection = "connecting";
  presence: string[] = [];
  /** Highest event sequence number applied. */
  seq = 0;
  /** Newest first, for activity feeds. */
  feed: Event[] = [];

  private base = emptyTables();
  private tombs = new Map<string, number>();
  private pending = new Map<string, Patch[]>();
  private cache: Tables | null = null;
  private version = 0;
  private listeners = new Set<() => void>();
  private feedSeqs = new Set<number>();

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  getVersion = () => this.version;

  private changed() {
    this.cache = null;
    this.version++;
    for (const l of this.listeners) l();
  }

  /**
   * Copy-on-write: a table that changes gets a new Map, and the tables object a new identity,
   * so anything memoized on them recomputes. Tables that did not change keep their identity.
   */
  private writable<K extends TableName>(table: K, copied: Set<TableName>): Map<string, Entity> {
    if (!copied.has(table)) {
      if (!copied.size) this.base = { ...this.base };
      (this.base as Record<TableName, Map<string, Entity>>)[table] = new Map(this.base[table] as Map<string, Entity>);
      copied.add(table);
    }
    return this.base[table] as Map<string, Entity>;
  }

  /** Server state with optimistic patches applied. Cached until the next change. */
  get t(): Tables {
    if (this.cache) return this.cache;
    if (!this.pending.size) return (this.cache = this.base);
    const out = { ...this.base };
    const copied = new Set<TableName>();
    for (const patches of this.pending.values()) {
      for (const p of patches) {
        if (!copied.has(p.table)) {
          (out as Record<TableName, Map<string, Entity>>)[p.table] = new Map(this.base[p.table] as Map<string, Entity>);
          copied.add(p.table);
        }
        const m = out[p.table] as Map<string, Entity>;
        if (p.value === null || isGone(p.table, p.value)) m.delete(p.id);
        else m.set(p.id, p.value);
      }
    }
    return (this.cache = out);
  }

  load(s: Snapshot) {
    this.base = emptyTables();
    this.tombs.clear();
    this.me = s.me;
    this.myRoles = s.myRoles;
    const put = <K extends TableName>(table: K, rows: Entity[], key: (r: Entity) => string = (r) => (r as { id: string }).id) => {
      const m = this.base[table] as Map<string, Entity>;
      for (const r of rows) m.set(key(r), r);
    };
    put("actors", s.actors);
    put("members", s.members, (r) => `${(r as Member).spaceId}:${(r as Member).actorId}`);
    put("spaces", s.spaces);
    put("labels", s.labels);
    put("templates", s.templates);
    put("kinds", s.kinds);
    put("nodes", s.nodes);
    put("edges", s.edges);
    put("claims", s.claims, (r) => (r as Claim).nodeId);
    put("refs", s.refs);
    put("plans", s.plans);
    put("views", s.views);
    for (const tomb of s.tombstones) {
      const table = TABLE_OF[tomb.entity];
      if (!(this.base[table] as Map<string, Entity>).has(tomb.id)) this.tombs.set(`${table}:${tomb.id}`, tomb.seq);
    }
    this.seq = Math.max(this.seq, s.seq);
    this.status = "ready";
    this.changed();
  }

  applyEvents(events: Event[]) {
    if (!events.length) return;
    const copied = new Set<TableName>();
    let feed: Event[] | null = null;
    for (const e of events) {
      const table = TABLE_OF[e.entity];
      const tombKey = `${table}:${e.entityId}`;
      const current = (this.base[table] as Map<string, Entity>).get(e.entityId) as { seq: number } | undefined;
      const known = current?.seq ?? this.tombs.get(tombKey) ?? 0;
      if (e.seq > this.seq) this.seq = e.seq;
      if (!this.feedSeqs.has(e.seq)) {
        this.feedSeqs.add(e.seq);
        (feed ??= [...this.feed]).push(e);
      }
      if (e.seq <= known) continue;
      const map = this.writable(table, copied);
      if (e.op === "delete" || isGone(table, e.data as Entity)) {
        map.delete(e.entityId);
        this.tombs.set(tombKey, e.seq);
      } else {
        map.set(e.entityId, e.data as Entity);
        this.tombs.delete(tombKey);
      }
      if (table === "actors" && this.me && e.entityId === this.me.id) this.me = e.data as Actor;
    }
    if (feed) {
      // A new array when history changes, so feeds memoized on it recompute even if no table did.
      feed.sort((a, b) => b.seq - a.seq);
      if (feed.length > 1500) for (const e of feed.splice(1500)) this.feedSeqs.delete(e.seq);
      this.feed = feed;
    }
    this.changed();
  }

  /** Loads comments for one node, from the node detail endpoint. */
  putComments(list: Comment[]) {
    const fresh = list.filter((c) => (this.base.comments.get(c.id)?.seq ?? 0) < c.seq);
    if (!fresh.length) return;
    const m = this.writable("comments", new Set());
    for (const c of fresh) m.set(c.id, c);
    this.changed();
  }

  addPending(id: string, patches: Patch[]) {
    if (!patches.length) return;
    this.pending.set(id, patches);
    this.changed();
  }

  dropPending(id: string) {
    if (this.pending.delete(id)) this.changed();
  }

  setConnection(c: Connection) {
    if (this.connection === c) return;
    this.connection = c;
    this.changed();
  }

  setPresence(ids: string[]) {
    this.presence = ids;
    this.changed();
  }

  setStatus(s: Status, error = "") {
    this.status = s;
    this.error = error;
    this.changed();
  }
}

export const store = new Store();
export { TABLES };

/** Re-renders when the store changes. Components derive what they need from `store.t`. */
export function useStoreVersion(): number {
  return useSyncExternalStore(store.subscribe, store.getVersion);
}

/** Recent events, newest first. The array changes identity whenever history does. */
export function useFeed(): Event[] {
  useStoreVersion();
  return store.feed;
}

export function useTables(): Tables {
  useStoreVersion();
  return store.t;
}
